import React from "react";
import { GoogleLogin } from "@react-oauth/google";
import toast from "react-hot-toast";
import styles from "./GoogleSignInButton.module.css";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

/**
 * Official multicolor Google "G" mark. Renders inside our visible
 * Figma button (both the no-client-id fallback and the overlay-on-top
 * button when the client id is set).
 */
const GoogleGMark = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.1A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.44.36-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.43 3.44 1.18 4.93l3.66-2.83z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"
    />
  </svg>
);

const FigmaButtonContent = () => (
  <>
    <GoogleGMark size={18} />
    <span className={styles.label}>
      <span className={styles.labelLight}>Sign in with</span>
      {" "}
      <span className={styles.labelDark}>Google</span>
    </span>
  </>
);

/**
 * Sign in with Google.
 *
 * Architecture: click-passthrough overlay. The real `<GoogleLogin>`
 * (from `@react-oauth/google`) renders Google's iframe button at
 * `opacity: 0` underneath. Our pixel-perfect Figma custom button sits
 * on top with `pointer-events: none`. Clicks land on Google's
 * invisible button -> popup opens. The visible button is a paint
 * layer; all functionality and accessibility live on Google's button.
 *
 * Props:
 * - onCredential(credential: string): called with the Google id_token
 *   after the user picks an account. Parent POSTs to /users/auth/google/.
 * - onError(): forwarded from GoogleLogin's onError. Fires when Google
 *   itself rejects the credential exchange or the user cancels the
 *   FedCM prompt. Parent routes this through googleSignInErrors.js
 *   which decides whether to surface a message.
 * - loading / disabled: visual disabled state.
 *
 * When `REACT_APP_GOOGLE_CLIENT_ID` is empty, renders a plain Figma
 * button (no overlay, no GoogleLogin) that emits a "not configured"
 * toast on click. Lets the page render safely in environments where
 * Google sign-in hasn't been provisioned yet.
 */
const GoogleSignInButton = ({
  onCredential,
  onError,
  loading = false,
  disabled = false,
}) => {
  if (!GOOGLE_CLIENT_ID) {
    return (
      <button
        type="button"
        className={styles.button}
        onClick={() =>
          toast.error(
            "Google sign-in is not configured for this environment. Please use your TOCA email and password."
          )
        }
        aria-label="Sign in with Google"
      >
        <FigmaButtonContent />
      </button>
    );
  }

  return (
    <div
      className={`${styles.overlayWrap} ${
        loading || disabled ? styles.overlayDisabled : ""
      }`}
      aria-busy={loading || undefined}
    >
      <GoogleLogin
        onSuccess={(resp) => {
          if (resp?.credential && !loading && !disabled) {
            onCredential?.(resp.credential);
          }
        }}
        onError={() => onError?.()}
        theme="outline"
        size="large"
        text="signin_with"
        shape="rectangular"
        width="340"
        logo_alignment="left"
      />
      <button
        type="button"
        className={`${styles.button} ${styles.overlayButton}`}
        aria-hidden="true"
        tabIndex={-1}
      >
        <FigmaButtonContent />
      </button>
    </div>
  );
};

export default GoogleSignInButton;
